import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../../layouts/default";
import { Row, Col, Form, Button, Table, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SmartPagination from "../../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
  fetchViCtsTransferSettingHistoricalData,
  setTransferRateDispatcher,
} from "../../../store/slices/viCts/viCts";
import { convertToIndianDateTime, hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

function TransferSetting() {
  const dispatch = useDispatch();

  const {
    list = [],
    loading = false,
    pagination = {},
  } = useSelector((store) => store.viCtsReducer.transferSettingList);

  const [showModal, setShowModal] = useState(false);
  const [cqTransferRate, setCqTransferRate] = useState("");
  const [ftTransferRate, setFtTransferRate] = useState("");

  const [filters, setFilters] = useState({
    modifiedBy: "",
    date: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    modifiedBy: "",
    date: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const fetchData = useCallback(
    async (page = 1, filtersToUse = appliedFilters) => {
      try {
        await dispatch(
          fetchViCtsTransferSettingHistoricalData({
            page,
            limit,
            filters: filtersToUse,
          })
        ).unwrap();
      } catch (err) {
        toast.error(err?.message || "Internal Server Error!");
      }
    },
    [dispatch, appliedFilters]
  );

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchData(1, filters);
  };

  const handleClearFilters = () => {
    const cleared = { modifiedBy: "", date: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
    fetchData(1, cleared);
  };

  const handleOpenModal = () => {
    const latest = list?.[0];
    if (latest) {
      setCqTransferRate(
        latest.cqTransferRate !== undefined
          ? String(latest.cqTransferRate)
          : ""
      );
      setFtTransferRate(
        latest.ftTransferRate !== undefined
          ? String(latest.ftTransferRate)
          : ""
      );
    }
    setShowModal(true);
  };

  const handleSubmitRate = async () => {
    if (cqTransferRate === "" && ftTransferRate === "") {
      toast.error("At least one transfer rate is required.");
      return;
    }

    if (cqTransferRate !== "" && Number(cqTransferRate) < 0) {
      toast.error("CQ Transfer Rate must be a non-negative number.");
      return;
    }

    if (ftTransferRate !== "" && Number(ftTransferRate) < 0) {
      toast.error("FT Transfer Rate must be a non-negative number.");
      return;
    }

    const latest = list?.[0];

    if (
      latest &&
      Number(cqTransferRate) === Number(latest.cqTransferRate) &&
      Number(ftTransferRate) === Number(latest.ftTransferRate)
    ) {
      toast.info("No changes detected.");
      setShowModal(false);
      return;
    }

    try {
      const payload = {};
      if (cqTransferRate !== "") payload.cqTransferRate = Number(cqTransferRate);
      if (ftTransferRate !== "") payload.ftTransferRate = Number(ftTransferRate);

      const msg = await dispatch(setTransferRateDispatcher(payload)).unwrap();
      console.log("msg : ", msg)
      toast.success(msg);

      setShowModal(false);
      fetchData(currentPage);
    } catch (err) {
      toast.error(err || err?.message || "Something went wrong.");
    }
  };

  if (!hasPermission(PERMISSIONS.VI_CTS.VIEW)) {
    return (
      <Layout>
        <div>
          <h3>You Do not have permission to view this screen</h3>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-semibold mb-0">VI-CTS Transfer Setting</h4>
        {
          hasPermission(PERMISSIONS.VI_CTS.EDIT) && hasPermission(PERMISSIONS.VI_CTS.CREATE) && <Button variant="primary" onClick={handleOpenModal}>
            Set Transfer Rate
          </Button>
        }
      </div>

      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="align-items-end g-2">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Modified By</Form.Label>
              <Form.Control
                type="text"
                name="modifiedBy"
                value={filters.modifiedBy}
                placeholder="Search by Admin Name"
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                name="date"
                value={filters.date}
                max={new Date().toISOString().split("T")[0]}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col md={4} className="text-end">
            <Button
              variant="primary"
              className="me-2"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
            <Button variant="secondary" onClick={handleClearFilters}>
              Clear
            </Button>
          </Col>
        </Row>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>CQ Transfer Count</th>
            <th>FT Transfer Count</th>
            <th>Modified By</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="text-center">
                <Spinner animation="border" size="sm" /> Loading...
              </td>
            </tr>
          ) : list.length ? (
            list.map((item, index) => (
              <tr key={item._id}>
                <td>{(currentPage - 1) * limit + index + 1}</td>
                <td>{convertToIndianDateTime(item?.createdAt) || "NA"}</td>
                <td>{Number(item.cqTransferRate).toLocaleString()}</td>
                <td>{Number(item.ftTransferRate).toLocaleString()}</td>
                <td>{item?.user?.name || "-"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="text-center">
                No records found!!
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {pagination?.totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <SmartPagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Transfer Rate</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>CQ Transfer Rate</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter CQ transfer rate"
              value={cqTransferRate}
              onChange={(e) => setCqTransferRate(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>FT Transfer Rate</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter FT transfer rate"
              value={ftTransferRate}
              onChange={(e) => setFtTransferRate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitRate}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default TransferSetting;
